import React from 'react';
import {
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import SearchableSelect from '../SearchableSelect';
import {
  IMPRESION_MOTOR,
  IMPRESION_VARIANTE,
  type ImpresionMotor,
} from '../../constants/sucVentaImpresion';
import type {
  SucVentaCatalogoImpresion,
  SucVentaImpresionLetra,
} from '../../services/sucVentaService';

interface ImpresionLetraCardProps {
  titulo: string;
  impresion: SucVentaImpresionLetra;
  impresora: string;
  catalogo: SucVentaCatalogoImpresion;
  onImpresionChange: (patch: Partial<SucVentaImpresionLetra>) => void;
  onImpresoraChange: (value: string) => void;
}

const ImpresionLetraCard: React.FC<ImpresionLetraCardProps> = ({
  titulo,
  impresion,
  impresora,
  catalogo,
  onImpresionChange,
  onImpresoraChange,
}) => {
  const motor = impresion.motor ?? IMPRESION_MOTOR.PLANTILLA;
  const esCrystal = motor === IMPRESION_MOTOR.CRYSTAL;

  const motorOptions =
    catalogo.motores.length > 0
      ? catalogo.motores
      : [
          { valor: IMPRESION_MOTOR.PLANTILLA, etiqueta: 'Plantillas' },
          { valor: IMPRESION_MOTOR.CRYSTAL, etiqueta: 'Crystal (.rpt)' },
        ];

  const varianteOptions =
    catalogo.variantesPlantilla.length > 0
      ? catalogo.variantesPlantilla
      : [
          { valor: IMPRESION_VARIANTE.PLANTILLA_1, etiqueta: 'Plantilla 1 (actual)' },
          { valor: IMPRESION_VARIANTE.PLANTILLA_2, etiqueta: 'Plantilla 2 (antigua)' },
        ];

  const handleMotorChange = (nuevoMotor: ImpresionMotor) => {
    onImpresionChange({ motor: nuevoMotor });
  };

  return (
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {titulo}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  Motor de impresión
                </FormLabel>
                <RadioGroup
                  row
                  value={motor}
                  onChange={(e) => handleMotorChange(e.target.value as ImpresionMotor)}
                >
                  {motorOptions.map((opcion) => (
                    <FormControlLabel
                      key={opcion.valor}
                      value={opcion.valor}
                      control={<Radio size="small" />}
                      label={opcion.etiqueta}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8} sx={{ display: esCrystal ? 'block' : 'none' }}>
                <TextField
                  label="Ruta del archivo .rpt"
                  value={impresion.rutaCrystal ?? ''}
                  onChange={(e) => onImpresionChange({ rutaCrystal: e.target.value })}
                  fullWidth
                  placeholder="C:\GPC\Reportes\Ventas\Factura.rpt"
                  helperText="La ruta se conserva aunque use Plantillas. Crystal no genera PDF en la web."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            <Grid item xs={12} md={6} sx={{ display: esCrystal ? 'none' : 'block' }}>
                <SearchableSelect
                  label="Variante de plantilla"
                  value={impresion.variantePlantilla ?? IMPRESION_VARIANTE.PLANTILLA_1}
                  onChange={(val) =>
                    onImpresionChange({
                      variantePlantilla: val ?? IMPRESION_VARIANTE.PLANTILLA_1,
                    })
                  }
                  options={varianteOptions.map((v) => ({ value: v.valor, label: v.etiqueta }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

            <Grid item xs={12} md={esCrystal ? 4 : 6}>
              <TextField
                label="Impresora"
                value={impresora}
                onChange={(e) => onImpresoraChange(e.target.value)}
                fullWidth
                helperText="Nombre exacto como aparece en Windows."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ImpresionLetraCard;
